import { useSyncExternalStore } from 'react';
import type { Locale } from '../data/presentation-types';
import { normalizeLocale, syncDocumentLocale } from './locale';

// ---------------------------------------------------------------------------
// Store externe léger — permet à tout composant de lire ET modifier la locale
// sans dépendre du hook complet `useAscendSession`.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ascend.ui.preferences.v1';

let currentLocale: Locale = 'fr-FR';
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): Locale {
  return currentLocale;
}

function persistLocaleToStorage(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const prefs: Record<string, unknown> = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    prefs.locale = locale;
    if (prefs.presentationPreferences && typeof prefs.presentationPreferences === 'object') {
      (prefs.presentationPreferences as Record<string, unknown>).locale = locale;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage inaccessible
  }
}

/**
 * Initialise le store depuis localStorage au démarrage du module.
 */
function initFromStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return;
    const locale = (parsed as Record<string, unknown>).locale;
    if (typeof locale === 'string') {
      const normalized = normalizeLocale(locale);
      currentLocale = normalized;
      syncDocumentLocale(normalized);
    }
  } catch {
    // localStorage inaccessible → garder fr-FR
  }
}

// Initialisation unique au chargement du module
initFromStorage();

/** Appelé par `useAscendSession.changeLocale` pour notifier tous les lecteurs. */
export function notifyLocaleChange(locale: Locale): void {
  currentLocale = locale;
  syncDocumentLocale(locale);
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Définit la locale active, persiste dans localStorage, notifie tous les
 * lecteurs et synchronise `document.documentElement.lang`.
 * Autorité unique pour le changement de locale — utilisable depuis n'importe
 * quel composant (SettingsPage, useAscendSession, etc.).
 */
export function setLocale(raw: string): Locale {
  const locale = normalizeLocale(raw);
  currentLocale = locale;
  persistLocaleToStorage(locale);
  syncDocumentLocale(locale);
  for (const listener of listeners) {
    listener();
  }
  return locale;
}

/** Reset explicite pour les tests : force la locale sans toucher localStorage. */
export function resetLocaleForTest(locale: Locale = 'fr-FR'): void {
  currentLocale = locale;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Hook minimal : retourne la locale active.
 */
export function useAscendLocale(): Locale {
  return useSyncExternalStore<Locale>(subscribe, getSnapshot, () => 'fr-FR');
}
